import type { TFunction } from 'i18next'
import type { NotificationSummary } from '@proaxiv/server-api'

/** Localized title and body ready for display in the notification UI. */
export type FormattedNotification = {
  title: string
  message: string
}

type NotifyParams = Record<string, string>

/**
 * Resolves localized title/body for an in-app notification from its `type` + `params`.
 * Falls back to the stored English `title`/`message` for unknown types or legacy rows.
 */
export function formatNotification(
  notification: NotificationSummary,
  t: TFunction
): FormattedNotification {
  const params = (notification.params ?? {}) as NotifyParams
  const fallback = {
    title: notification.title || notification.type,
    message: notification.message ?? '',
  }

  switch (notification.type) {
    case 'ORG_CREATED':
      return {
        title: t('notif_org_created_title'),
        message: t('notif_org_created_message', params),
      }
    case 'ORG_APPROVED':
      return {
        title: t('notif_org_approved_title'),
        message: t('notif_org_approved_message', params),
      }
    case 'ORG_REJECTED':
      return {
        title: t('notif_org_rejected_title'),
        message: params.reason
          ? t('notif_org_rejected_message_with_reason', params)
          : t('notif_org_rejected_message', params),
      }
    case 'ORG_SUSPENDED':
      return {
        title: t('notif_org_suspended_title'),
        message: t('notif_org_suspended_message', params),
      }
    case 'ORG_INVITED':
      return {
        title: t('notif_org_invited_title'),
        message: t('notif_org_invited_message', params),
      }
    case 'ORG_MEMBER_REMOVED':
      return {
        title: t('notif_org_member_removed_title'),
        message: t('notif_org_member_removed_message', params),
      }
    case 'ORG_MEMBER_JOINED':
      return {
        title: t('notif_org_member_joined_title'),
        message: t('notif_org_member_joined_message', params),
      }
    case 'EVENT_PARTICIPANT_REGISTERED':
      return {
        title: t('notif_event_participant_registered_title'),
        message: t('notif_event_participant_registered_message', params),
      }
    case 'EVENT_PRESENTATION_UPLOADED':
      return {
        title: t('notif_event_presentation_uploaded_title'),
        message: t('notif_event_presentation_uploaded_message', params),
      }
    default:
      return fallback
  }
}
